import React, { memo, useMemo, useState, useCallback } from 'react';
import { FormSelect, FormSelectItem } from '@core/components/form';
import { CategoryAccordionContent } from './CategoryAccordionContent';
import { Button } from 'reactstrap';
import { Accordian, AccordianMapping } from '@modules/common/Accordian';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FormArray, FormControl } from '@core/utils/form';
import { CategoryId } from '@business/entities/category';
import { ProposalDisplayValueEnum } from '@business/entities/order/OrderProductResponse';
import { ItemConsiderationCategoryImageFormData, ItemConsiderationCategoryItemForm } from '../ItemConsiderationBlock';

interface CategoryItemProps {
  categoryCtrl: ItemConsiderationCategoryItemForm;
  categoryIndex: number;
  categoryItems: FormSelectItem<any, any>[];
  getCategoryImage: (categoryId: CategoryId) => ItemConsiderationCategoryImageFormData;
  updateCategoryImage: (categoryId: CategoryId, imageData: Partial<ItemConsiderationCategoryImageFormData>) => void;
  onDelete: () => void;
}

export const CategoryItem = memo((props: CategoryItemProps) => {
  const { categoryCtrl, categoryIndex, categoryItems, getCategoryImage, updateCategoryImage, onDelete } = props;

  // Force update state to trigger re-renders
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => forceUpdate({}), []);

  // Use form observables directly
  const categoryValue = useNonNilObservable(categoryCtrl.value$);

  // Get image data based on current category ID from form - always fresh, no memo
  const imageData = getCategoryImage(categoryValue.categoryId);
  
  // Wrapper for updateCategoryImage that forces re-render
  const handleUpdateCategoryImage = useCallback((categoryId: CategoryId, imageDataUpdate: Partial<ItemConsiderationCategoryImageFormData>) => {
    updateCategoryImage(categoryId, imageDataUpdate);
    // Gentle re-render trigger
    triggerUpdate();
  }, [updateCategoryImage, triggerUpdate]);

  // Get the selected category name using form data
  const selectedCategory = useMemo(() => {
    return categoryItems.find(item => item.value === categoryValue.categoryId);
  }, [categoryItems, categoryValue.categoryId]);

  const categoryName = selectedCategory ? selectedCategory.label : `Category ${categoryIndex + 1}`;

  // Create accordion tabs mapping
  const tabs: AccordianMapping[] = useMemo(
    () => [
      {
        title: (
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>{categoryName}</span>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <i className="fas fa-trash"></i>
            </Button>
          </div>
        ),
        component: CategoryAccordionContent,
        props: {
          categoryCtrl,
          categoryIndex,
          categoryItems,
          categoryId: categoryValue.categoryId,
          imageData,
          updateCategoryImage: handleUpdateCategoryImage,
        },
      },
    ],
    [categoryName, categoryCtrl, categoryIndex, categoryItems, categoryValue.categoryId, imageData, handleUpdateCategoryImage, onDelete],
  );

  return (
    <div className="mb-3">
      <Accordian key={categoryValue.categoryId} tabs={tabs} initialIndex={-1} />
    </div>
  );
});
